import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useOrgProject } from '../contexts/OrgProjectContext';
import PageLayout from '../components/PageLayout';
import { apiFetch } from '../apiClient';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// ─── Design Tokens ────────────────────────────────────────────────────────────
const C = {
  navy:       '#0F172A',
  navyMid:    '#1E293B',
  navyLight:  '#334155',
  blue:       '#3B82F6',
  blueLt:     '#DBEAFE',
  green:      '#10B981',
  white:      '#FFFFFF',
  slate:      '#64748B',
  slateLight: '#94A3B8',
  border:     '#E2E8F0',
  bg:         '#F8FAFC',
  text:       '#1E293B',
  textMid:    '#475569',
  red:        '#EF4444',
  redLt:      '#FEE2E2',
  yellow:     '#F59E0B',
  yellowLt:   '#FEF3C7',
};

// ─── Constants ────────────────────────────────────────────────────────────────
const PAGE_SIZE = 50;
const REPO_PAGE_SIZE = 50;
const MAX_IMPORT_SELECTION = 50;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatTimestamp(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function sourceLabel(source) {
  switch (source) {
    case 'manual': return 'Manual';
    case 'scm_import': return 'SCM Import';
    case 'filesystem_scan': return 'Filesystem';
    default: return source || '—';
  }
}

// ─── Validation ───────────────────────────────────────────────────────────────

function validateAppName(name) {
  if (!name || name.trim().length === 0) return 'Name is required';
  if (name.length > 128) return 'Name must be 128 characters or fewer';
  return null;
}

function validateSourceLocation(repoUrl, localPath) {
  if ((!repoUrl || repoUrl.trim().length === 0) && (!localPath || localPath.trim().length === 0)) {
    return 'At least one of Repository URL or Local Path is required';
  }
  return null;
}

function validateRepoUrl(url) {
  if (url && url.length > 2048) return 'Repository URL must be 2048 characters or fewer';
  return null;
}

function validateLocalPath(path) {
  if (path && path.length > 2048) return 'Local path must be 2048 characters or fewer';
  return null;
}

function validateLanguage(lang) {
  if (lang && lang.length > 64) return 'Language must be 64 characters or fewer';
  return null;
}

function validateFramework(fw) {
  if (fw && fw.length > 64) return 'Framework must be 64 characters or fewer';
  return null;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function AppRegistry() {
  const navigate = useNavigate();
  const { orgId: routeOrgId, projectId: routeProjectId } = useParams();
  const { orgId, projectId, syncFromRouteParams } = useOrgProject();

  // Sync context from route params on mount
  useEffect(() => {
    if (routeOrgId && routeProjectId) {
      syncFromRouteParams(routeOrgId, routeProjectId);
    }
  }, [routeOrgId, routeProjectId, syncFromRouteParams]);

  // ── App list state ──────────────────────────────────────────────────────────
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  // ── Registration form state ─────────────────────────────────────────────────
  const [showForm, setShowForm] = useState(false);
  const [formName, setFormName] = useState('');
  const [formRepoUrl, setFormRepoUrl] = useState('');
  const [formLocalPath, setFormLocalPath] = useState('');
  const [formLanguage, setFormLanguage] = useState('');
  const [formFramework, setFormFramework] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formApiError, setFormApiError] = useState(null);

  // ── Bulk import state ───────────────────────────────────────────────────────
  const [scmConnections, setScmConnections] = useState([]);
  const [scmLoading, setScmLoading] = useState(false);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [importProgress, setImportProgress] = useState(null); // { total, completed } or null
  const [importResult, setImportResult] = useState(null); // { created: [], skipped: [] } or null

  // ── Project source type state ───────────────────────────────────────────────
  const [projectSourceType, setProjectSourceType] = useState(null); // filesystem | git_repo | s3_bucket

  // ── Filesystem folder browser state ─────────────────────────────────────────
  const [fsFolders, setFsFolders] = useState([]);
  const [fsFoldersLoading, setFsFoldersLoading] = useState(false);
  const [fsFoldersError, setFsFoldersError] = useState(null);

  // ── Repo browser state ──────────────────────────────────────────────────────
  const [repos, setRepos] = useState([]);
  const [reposLoading, setReposLoading] = useState(false);
  const [reposError, setReposError] = useState(null);
  const [repoPage, setRepoPage] = useState(1);
  const [selectedRepos, setSelectedRepos] = useState(new Set());
  const [selectedConnectionId, setSelectedConnectionId] = useState(null);
  const dialogRef = useRef(null);

  // ── Toast state ─────────────────────────────────────────────────────────────
  const [toast, setToast] = useState(null);

  // ── Fetch applications ──────────────────────────────────────────────────────
  const fetchApps = useCallback(async () => {
    if (!orgId || !projectId) return;
    setLoading(true);
    setListError(null);
    try {
      const res = await apiFetch(
        `/api/orgs/${orgId}/projects/${projectId}/apps`
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed to load applications (${res.status})`);
      }
      const data = await res.json();
      const appList = Array.isArray(data) ? data : (data.data || data.results || data.items || []);
      setApps(appList);
    } catch (err) {
      setListError(err.message);
    } finally {
      setLoading(false);
    }
  }, [orgId, projectId]);

  useEffect(() => {
    fetchApps();
  }, [fetchApps]);

  // ── Fetch SCM connections (to determine bulk-import availability) ───────────
  const fetchSCMConnections = useCallback(async () => {
    if (!orgId) return;
    setScmLoading(true);
    try {
      const res = await apiFetch(`/api/orgs/${orgId}/scm-connections`);
      if (res.ok) {
        const data = await res.json();
        const connections = Array.isArray(data) ? data : (data.data || data.results || data.items || []);
        setScmConnections(connections.filter(c => c.status === 'active'));
      } else {
        setScmConnections([]);
      }
    } catch {
      setScmConnections([]);
    } finally {
      setScmLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchSCMConnections();
  }, [fetchSCMConnections]);

  // ── Fetch project source_type ───────────────────────────────────────────────
  const fetchProjectSourceType = useCallback(async () => {
    if (!orgId || !projectId) return;
    try {
      const res = await apiFetch(`/api/orgs/${orgId}/projects/${projectId}`);
      if (res.ok) {
        const data = await res.json();
        setProjectSourceType(data.source_type || null);
      }
    } catch {
      setProjectSourceType(null);
    }
  }, [orgId, projectId]);

  useEffect(() => {
    fetchProjectSourceType();
  }, [fetchProjectSourceType]);

  // ── Fetch filesystem folders (for filesystem projects) ──────────────────────
  const fetchFsFolders = useCallback(async () => {
    if (!orgId || !projectId) return;
    setFsFoldersLoading(true);
    setFsFoldersError(null);
    try {
      const res = await apiFetch(
        `/api/orgs/${orgId}/projects/${projectId}/apps/folders`
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed to load folders (${res.status})`);
      }
      const data = await res.json();
      setFsFolders(data.folders || []);
    } catch (err) {
      setFsFoldersError(err.message);
      setFsFolders([]);
    } finally {
      setFsFoldersLoading(false);
    }
  }, [orgId, projectId]);

  // ── Toast auto-dismiss ──────────────────────────────────────────────────────
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // ── Fetch repos from SCM connection ─────────────────────────────────────────
  const fetchRepos = useCallback(async (connectionId) => {
    if (!orgId || !connectionId) return;
    setReposLoading(true);
    setReposError(null);
    try {
      const res = await apiFetch(
        `/api/orgs/${orgId}/scm-connections/${connectionId}/repos`
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        if (res.status === 401) {
          throw new Error(body.error || 'SCM connection credentials have expired. Please reconfigure the connection.');
        }
        if (res.status === 502) {
          throw new Error(body.error || 'Unable to reach the SCM provider. Please try again later.');
        }
        throw new Error(body.error || `Failed to load repositories (${res.status})`);
      }
      const data = await res.json();
      const repoList = Array.isArray(data) ? data : (data.results || data.repos || data.items || []);
      setRepos(repoList);
    } catch (err) {
      setReposError(err.message);
      setRepos([]);
    } finally {
      setReposLoading(false);
    }
  }, [orgId]);

  // ── Open bulk import modal ──────────────────────────────────────────────────
  const openBulkImport = useCallback(() => {
    setBulkImportOpen(true);
    setSelectedRepos(new Set());
    setRepoPage(1);
    setReposError(null);
    setFsFoldersError(null);

    if (projectSourceType === 'filesystem') {
      // Fetch filesystem folders for the modal
      fetchFsFolders();
    } else {
      // Use first active SCM connection by default
      const conn = scmConnections[0];
      if (conn) {
        setSelectedConnectionId(conn.id);
        fetchRepos(conn.id);
      }
    }
  }, [scmConnections, fetchRepos, fetchFsFolders, projectSourceType]);

  // ── Close bulk import modal ─────────────────────────────────────────────────
  const closeBulkImport = () => {
    setBulkImportOpen(false);
    setSelectedRepos(new Set());
    setRepos([]);
    setFsFolders([]);
    setReposError(null);
    setFsFoldersError(null);
    setRepoPage(1);
  };

  // ── Handle connection change in repo browser ────────────────────────────────
  const handleConnectionChange = (connectionId) => {
    setSelectedConnectionId(connectionId);
    setSelectedRepos(new Set());
    setRepoPage(1);
    fetchRepos(connectionId);
  };

  // ── Toggle repo selection ───────────────────────────────────────────────────
  const toggleRepoSelection = (repoName) => {
    setSelectedRepos(prev => {
      const next = new Set(prev);
      if (next.has(repoName)) {
        next.delete(repoName);
      } else {
        if (next.size >= MAX_IMPORT_SELECTION) return prev; // Enforce max 50
        next.add(repoName);
      }
      return next;
    });
  };

  // ── Select all on current page ──────────────────────────────────────────────
  const selectAllOnPage = () => {
    const pageItems = paginatedRepos.map(r => r.name || r);
    setSelectedRepos(prev => {
      const next = new Set(prev);
      for (const name of pageItems) {
        if (next.size >= MAX_IMPORT_SELECTION) break;
        next.add(name);
      }
      return next;
    });
  };

  // ── Clear selection ─────────────────────────────────────────────────────────
  const clearRepoSelection = () => {
    setSelectedRepos(new Set());
  };

  // ── Escape key to close modal ───────────────────────────────────────────────
  useEffect(() => {
    if (!bulkImportOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeBulkImport();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [bulkImportOpen]);

  // ── Sorting ─────────────────────────────────────────────────────────────────
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedApps = [...apps].sort((a, b) => {
    let aVal = a[sortField] || '';
    let bVal = b[sortField] || '';
    if (sortField === 'created_at') {
      aVal = new Date(aVal).getTime() || 0;
      bVal = new Date(bVal).getTime() || 0;
    } else {
      aVal = String(aVal).toLowerCase();
      bVal = String(bVal).toLowerCase();
    }
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // ── Pagination ──────────────────────────────────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(sortedApps.length / PAGE_SIZE));
  const paginatedApps = sortedApps.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  // ── Registration form handlers ─────────────────────────────────────────────
  const openForm = () => {
    setShowForm(true);
    setFormName('');
    setFormRepoUrl('');
    setFormLocalPath('');
    setFormLanguage('');
    setFormFramework('');
    setFormErrors({});
    setFormApiError(null);
  };

  const closeForm = () => {
    setShowForm(false);
    setFormErrors({});
    setFormApiError(null);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormApiError(null);

    // Validate all fields
    const errors = {};
    const nameErr = validateAppName(formName);
    if (nameErr) errors.name = nameErr;

    const sourceErr = validateSourceLocation(formRepoUrl, formLocalPath);
    if (sourceErr) errors.source = sourceErr;

    const repoErr = validateRepoUrl(formRepoUrl);
    if (repoErr) errors.repo_url = repoErr;

    const pathErr = validateLocalPath(formLocalPath);
    if (pathErr) errors.local_path = pathErr;

    const langErr = validateLanguage(formLanguage);
    if (langErr) errors.language = langErr;

    const fwErr = validateFramework(formFramework);
    if (fwErr) errors.framework = fwErr;

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    setFormSubmitting(true);

    try {
      const body = { name: formName.trim() };
      if (formRepoUrl.trim()) body.repo_url = formRepoUrl.trim();
      if (formLocalPath.trim()) body.local_path = formLocalPath.trim();
      if (formLanguage.trim()) body.language = formLanguage.trim();
      if (formFramework.trim()) body.framework = formFramework.trim();

      const res = await apiFetch(
        `/api/orgs/${orgId}/projects/${projectId}/apps`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }
      );

      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        if (errBody.code === 'DUPLICATE_FIELD' || (errBody.error && errBody.error.toLowerCase().includes('already'))) {
          setFormErrors(prev => ({ ...prev, name: 'An application with this name already exists in this project' }));
          return;
        }
        if (res.status >= 400 && res.status < 500) {
          setFormApiError(errBody.error || 'Validation error');
        } else {
          setToast({ type: 'error', message: errBody.error || `Server error (${res.status})` });
        }
        return;
      }

      closeForm();
      setToast({ type: 'success', message: 'Application registered successfully' });
      fetchApps();
    } catch (err) {
      setToast({ type: 'error', message: err.message || 'Network error' });
    } finally {
      setFormSubmitting(false);
    }
  };

  // ── Bulk import handler ─────────────────────────────────────────────────────
  const handleBulkImport = async (names) => {
    if (!names || names.length === 0) return;

    setImportProgress({ total: names.length, completed: 0 });
    closeBulkImport();
    try {
      // Simulate incremental progress (the API is a single call, but we show activity)
      const progressInterval = setInterval(() => {
        setImportProgress(prev => {
          if (!prev) return null;
          const next = Math.min(prev.completed + Math.ceil(prev.total * 0.15), prev.total - 1);
          return { ...prev, completed: next };
        });
      }, 400);

      // Send appropriate payload key based on project source type
      const bodyKey = projectSourceType === 'filesystem' ? 'folder_names' : 'repo_identifiers';
      const res = await apiFetch(
        `/api/orgs/${orgId}/projects/${projectId}/apps/import`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ [bodyKey]: names }),
        }
      );

      clearInterval(progressInterval);

      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        setToast({ type: 'error', message: errBody.error || `Import failed (${res.status})` });
        setImportProgress(null);
        return;
      }

      const result = await res.json();
      const created = result.created || [];
      const skipped = result.skipped || [];

      // Show completed progress briefly
      setImportProgress({ total: names.length, completed: names.length });
      await new Promise(resolve => setTimeout(resolve, 500));

      setImportResult({ created, skipped });
      setImportProgress(null);
      setToast({
        type: 'success',
        message: `Successfully imported ${created.length} application${created.length !== 1 ? 's' : ''}${skipped.length > 0 ? ` (${skipped.length} skipped)` : ''}`,
      });
      fetchApps();
    } catch (err) {
      setToast({ type: 'error', message: err.message || 'Import failed' });
      setImportProgress(null);
    }
  };

  // ── Inline validation on change ─────────────────────────────────────────────
  const handleNameChange = (val) => {
    setFormName(val);
    const err = validateAppName(val);
    setFormErrors(prev => {
      const next = { ...prev };
      if (err) next.name = err; else delete next.name;
      return next;
    });
    if (formApiError) setFormApiError(null);
  };

  const handleRepoUrlChange = (val) => {
    setFormRepoUrl(val);
    const err = validateRepoUrl(val);
    setFormErrors(prev => {
      const next = { ...prev };
      if (err) next.repo_url = err; else delete next.repo_url;
      // Clear source-level error if either field now has a value
      if (val.trim() || formLocalPath.trim()) delete next.source;
      return next;
    });
  };

  const handleLocalPathChange = (val) => {
    setFormLocalPath(val);
    const err = validateLocalPath(val);
    setFormErrors(prev => {
      const next = { ...prev };
      if (err) next.local_path = err; else delete next.local_path;
      // Clear source-level error if either field now has a value
      if (formRepoUrl.trim() || val.trim()) delete next.source;
      return next;
    });
  };

  const handleLanguageChange = (val) => {
    setFormLanguage(val);
    const err = validateLanguage(val);
    setFormErrors(prev => {
      const next = { ...prev };
      if (err) next.language = err; else delete next.language;
      return next;
    });
  };

  const handleFrameworkChange = (val) => {
    setFormFramework(val);
    const err = validateFramework(val);
    setFormErrors(prev => {
      const next = { ...prev };
      if (err) next.framework = err; else delete next.framework;
      return next;
    });
  };

  // ── Determine if bulk import is available ───────────────────────────────────
  const hasSCMConnection = scmConnections.length > 0;
  const isFilesystemProject = projectSourceType === 'filesystem';
  const isGitProject = projectSourceType === 'git_repo';
  const canBulkImport = isFilesystemProject || (isGitProject && hasSCMConnection);

  // ── Sort indicator ──────────────────────────────────────────────────────────
  const sortIndicator = (field) => {
    if (sortField !== field) return ' ↕';
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  // ── Repo/folder browser pagination ──────────────────────────────────────────
  const browseItems = isFilesystemProject
    ? fsFolders.filter(f => !f.already_imported)
    : repos;
  const totalRepoPages = Math.max(1, Math.ceil(browseItems.length / REPO_PAGE_SIZE));
  const paginatedRepos = browseItems.slice(
    (repoPage - 1) * REPO_PAGE_SIZE,
    repoPage * REPO_PAGE_SIZE
  );

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <PageLayout>
    <div style={styles.container}>
      {/* Toast */}
      {toast && (
        <div style={{
          ...styles.toast,
          background: toast.type === 'success' ? C.green : C.red,
        }}>
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Application Registry</h1>
          <p style={styles.subtitle}>
            Manage applications within your project
          </p>
        </div>
        <div style={styles.headerActions}>
          <button
            onClick={() => canBulkImport ? openBulkImport() : null}
            disabled={!canBulkImport || scmLoading}
            style={{
              ...styles.bulkImportBtn,
              opacity: canBulkImport ? 1 : 0.5,
              cursor: canBulkImport ? 'pointer' : 'not-allowed',
            }}
            title={isFilesystemProject ? 'Import folders from filesystem' : (!hasSCMConnection ? 'An SCM connection must be configured first' : 'Import from SCM')}
            aria-label={isFilesystemProject ? 'Bulk import from filesystem' : 'Bulk import from SCM'}
          >
            ⬇ Bulk Import
          </button>
          <button onClick={openForm} style={styles.createBtn}>
            + Register App
          </button>
        </div>
      </div>

      {/* SCM connection warning — only show for git projects without SCM connection */}
      {!scmLoading && isGitProject && !hasSCMConnection && (
        <div style={styles.scmWarning} role="status">
          <span style={styles.scmWarningIcon}>ℹ</span>
          <span>
            Bulk import is unavailable — no active SCM connection is configured for this organisation.
            Configure an SCM connection to enable repository import.
          </span>
        </div>
      )}

      {/* Import result confirmation */}
      {importResult && (
        <div style={styles.importConfirmation} role="status">
          <span style={styles.importConfirmationIcon}>✓</span>
          <span>
            Imported {importResult.created.length} application{importResult.created.length !== 1 ? 's' : ''}.
            {importResult.skipped.length > 0 && (
              <> {importResult.skipped.length} skipped (already exist).</>
            )}
          </span>
          <button
            onClick={() => setImportResult(null)}
            style={styles.dismissBtn}
            aria-label="Dismiss import result"
          >
            ✕
          </button>
        </div>
      )}

      {/* Registration Form */}
      {showForm && (
        <div style={styles.formCard}>
          <h2 style={styles.formTitle}>Register Application</h2>
          <form onSubmit={handleFormSubmit}>
            {/* Name field */}
            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="app-name">
                Name <span style={styles.required}>*</span>
              </label>
              <input
                id="app-name"
                type="text"
                value={formName}
                onChange={(e) => handleNameChange(e.target.value)}
                maxLength={128}
                placeholder="Application name"
                style={{
                  ...styles.input,
                  borderColor: formErrors.name ? C.red : C.border,
                }}
                aria-invalid={!!formErrors.name}
                aria-describedby={formErrors.name ? 'app-name-error' : undefined}
              />
              {formErrors.name && (
                <span id="app-name-error" style={styles.fieldError}>{formErrors.name}</span>
              )}
              <span style={styles.charCount}>{formName.length}/128</span>
            </div>

            {/* Repository URL field */}
            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="app-repo-url">
                Repository URL
              </label>
              <input
                id="app-repo-url"
                type="text"
                value={formRepoUrl}
                onChange={(e) => handleRepoUrlChange(e.target.value)}
                maxLength={2048}
                placeholder="https://github.com/org/repo.git"
                style={{
                  ...styles.input,
                  borderColor: formErrors.repo_url ? C.red : C.border,
                }}
                aria-invalid={!!formErrors.repo_url}
                aria-describedby={formErrors.repo_url ? 'app-repo-error' : undefined}
              />
              {formErrors.repo_url && (
                <span id="app-repo-error" style={styles.fieldError}>{formErrors.repo_url}</span>
              )}
            </div>

            {/* Local Path field */}
            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="app-local-path">
                Local Path
              </label>
              <input
                id="app-local-path"
                type="text"
                value={formLocalPath}
                onChange={(e) => handleLocalPathChange(e.target.value)}
                maxLength={2048}
                placeholder="/path/to/application"
                style={{
                  ...styles.input,
                  borderColor: formErrors.local_path ? C.red : C.border,
                }}
                aria-invalid={!!formErrors.local_path}
                aria-describedby={formErrors.local_path ? 'app-path-error' : undefined}
              />
              {formErrors.local_path && (
                <span id="app-path-error" style={styles.fieldError}>{formErrors.local_path}</span>
              )}
            </div>

            {/* Source location error (at least one required) */}
            {formErrors.source && (
              <div style={styles.sourceError} role="alert">
                {formErrors.source}
              </div>
            )}

            {/* Language field */}
            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="app-language">
                Language
              </label>
              <input
                id="app-language"
                type="text"
                value={formLanguage}
                onChange={(e) => handleLanguageChange(e.target.value)}
                maxLength={64}
                placeholder="e.g. Python, Java, C#"
                style={{
                  ...styles.input,
                  borderColor: formErrors.language ? C.red : C.border,
                }}
                aria-invalid={!!formErrors.language}
                aria-describedby={formErrors.language ? 'app-lang-error' : undefined}
              />
              {formErrors.language && (
                <span id="app-lang-error" style={styles.fieldError}>{formErrors.language}</span>
              )}
            </div>

            {/* Framework field */}
            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="app-framework">
                Framework
              </label>
              <input
                id="app-framework"
                type="text"
                value={formFramework}
                onChange={(e) => handleFrameworkChange(e.target.value)}
                maxLength={64}
                placeholder="e.g. Django, Spring Boot, .NET"
                style={{
                  ...styles.input,
                  borderColor: formErrors.framework ? C.red : C.border,
                }}
                aria-invalid={!!formErrors.framework}
                aria-describedby={formErrors.framework ? 'app-fw-error' : undefined}
              />
              {formErrors.framework && (
                <span id="app-fw-error" style={styles.fieldError}>{formErrors.framework}</span>
              )}
            </div>

            {/* API error */}
            {formApiError && (
              <div style={styles.apiError} role="alert">
                {formApiError}
              </div>
            )}

            {/* Actions */}
            <div style={styles.formActions}>
              <button type="button" onClick={closeForm} style={styles.cancelBtn}>
                Cancel
              </button>
              <button
                type="submit"
                disabled={formSubmitting}
                style={{
                  ...styles.submitBtn,
                  opacity: formSubmitting ? 0.6 : 1,
                  cursor: formSubmitting ? 'not-allowed' : 'pointer',
                }}
              >
                {formSubmitting ? 'Registering…' : 'Register'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* List error */}
      {listError && (
        <div style={styles.listError} role="alert">
          {listError}
          <button onClick={() => fetchApps()} style={styles.retryBtn}>Retry</button>
        </div>
      )}

      {/* Import progress */}
      {importProgress && (
        <div style={styles.importProgressBar} role="status" aria-label="Import in progress">
          <div style={styles.importProgressText}>
            Importing applications… {importProgress.completed}/{importProgress.total}
          </div>
          <div style={styles.progressTrack}>
            <div style={{
              ...styles.progressFill,
              width: `${importProgress.total > 0 ? (importProgress.completed / importProgress.total) * 100 : 0}%`,
            }} />
          </div>
        </div>
      )}

      {/* Application table */}
      {loading ? (
        <div style={styles.loadingState}>Loading applications…</div>
      ) : apps.length === 0 && !listError ? (
        <div style={styles.emptyState}>
          <p style={styles.emptyText}>No applications registered yet.</p>
          <button onClick={openForm} style={styles.emptyBtn}>Register your first application</button>
        </div>
      ) : (
        <>
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th
                    style={{ ...styles.th, cursor: 'pointer' }}
                    onClick={() => handleSort('name')}
                    aria-sort={sortField === 'name' ? sortDirection + 'ending' : 'none'}
                  >
                    Name{sortIndicator('name')}
                  </th>
                  <th
                    style={{ ...styles.th, cursor: 'pointer' }}
                    onClick={() => handleSort('source')}
                    aria-sort={sortField === 'source' ? sortDirection + 'ending' : 'none'}
                  >
                    Source{sortIndicator('source')}
                  </th>
                  <th
                    style={{ ...styles.th, cursor: 'pointer' }}
                    onClick={() => handleSort('language')}
                    aria-sort={sortField === 'language' ? sortDirection + 'ending' : 'none'}
                  >
                    Language{sortIndicator('language')}
                  </th>
                  <th
                    style={{ ...styles.th, cursor: 'pointer' }}
                    onClick={() => handleSort('framework')}
                    aria-sort={sortField === 'framework' ? sortDirection + 'ending' : 'none'}
                  >
                    Framework{sortIndicator('framework')}
                  </th>
                  <th
                    style={{ ...styles.th, cursor: 'pointer' }}
                    onClick={() => handleSort('created_at')}
                    aria-sort={sortField === 'created_at' ? sortDirection + 'ending' : 'none'}
                  >
                    Registered{sortIndicator('created_at')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedApps.map(app => (
                  <tr key={app.id} style={styles.tr}>
                    <td style={styles.td}>
                      <span style={styles.appNameCell}>{app.name}</span>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.sourceBadge}>{sourceLabel(app.source)}</span>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.langCell}>{app.language || '—'}</span>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.fwCell}>{app.framework || '—'}</span>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.timestampCell}>{formatTimestamp(app.created_at)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={styles.pagination}>
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                style={{
                  ...styles.pageBtn,
                  opacity: currentPage === 1 ? 0.4 : 1,
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                }}
                aria-label="Previous page"
              >
                ← Prev
              </button>
              <span style={styles.pageInfo}>
                Page {currentPage} of {totalPages} ({apps.length} total)
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                style={{
                  ...styles.pageBtn,
                  opacity: currentPage === totalPages ? 0.4 : 1,
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                }}
                aria-label="Next page"
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}

      {/* Bulk Import Modal — SCM Repository / Filesystem Folder Browser */}
      {bulkImportOpen && (
        <div style={styles.overlay} role="presentation" onClick={closeBulkImport}>
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="bulk-import-title"
            style={styles.repoBrowserDialog}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={styles.repoBrowserHeader}>
              <div>
                <h3 id="bulk-import-title" style={styles.dialogTitle}>
                  {isFilesystemProject ? 'Import from Filesystem' : 'Import from SCM'}
                </h3>
                <p style={styles.repoBrowserSubtitle}>
                  {isFilesystemProject
                    ? `Select folders to import as applications (max ${MAX_IMPORT_SELECTION})`
                    : `Select repositories to import as applications (max ${MAX_IMPORT_SELECTION})`
                  }
                </p>
              </div>
              <button
                onClick={closeBulkImport}
                style={styles.repoBrowserCloseBtn}
                aria-label="Close dialog"
              >
                ✕
              </button>
            </div>

            {/* Connection selector (if multiple connections) — git_repo only */}
            {!isFilesystemProject && scmConnections.length > 1 && (
              <div style={styles.connectionSelector}>
                <label style={styles.connectionLabel} htmlFor="scm-conn-select">
                  SCM Connection:
                </label>
                <select
                  id="scm-conn-select"
                  value={selectedConnectionId || ''}
                  onChange={(e) => handleConnectionChange(e.target.value)}
                  style={styles.connectionSelect}
                >
                  {scmConnections.map(conn => (
                    <option key={conn.id} value={conn.id}>
                      {conn.connection_name} ({conn.provider} — {conn.scm_org_name})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Selection info bar */}
            <div style={styles.selectionBar}>
              <span style={styles.selectionCount}>
                {selectedRepos.size} of {MAX_IMPORT_SELECTION} selected
              </span>
              <div style={styles.selectionActions}>
                <button
                  onClick={selectAllOnPage}
                  disabled={(isFilesystemProject ? fsFoldersLoading : reposLoading) || paginatedRepos.length === 0}
                  style={styles.selectionActionBtn}
                >
                  Select page
                </button>
                <button
                  onClick={clearRepoSelection}
                  disabled={selectedRepos.size === 0}
                  style={styles.selectionActionBtn}
                >
                  Clear all
                </button>
              </div>
            </div>

            {/* Item list — repos or folders */}
            <div style={styles.repoListContainer}>
              {isFilesystemProject ? (
                /* Filesystem folder browser */
                fsFoldersLoading ? (
                  <div style={styles.repoLoadingState}>
                    Loading folders…
                  </div>
                ) : fsFoldersError ? (
                  <div style={styles.repoErrorState} role="alert">
                    <span style={styles.repoErrorIcon}>⚠</span>
                    <span>{fsFoldersError}</span>
                    <button
                      onClick={fetchFsFolders}
                      style={styles.repoRetryBtn}
                    >
                      Retry
                    </button>
                  </div>
                ) : browseItems.length === 0 ? (
                  <div style={styles.repoEmptyState}>
                    No new folders found in the project path (all may already be imported).
                  </div>
                ) : (
                  <>
                    {/* Table header */}
                    <div style={styles.repoTableHeader}>
                      <div style={styles.repoCheckboxCol}></div>
                      <div style={styles.repoNameCol}>Folder Name</div>
                    </div>

                    {/* Table rows */}
                    {paginatedRepos.map(folder => {
                      const name = folder.name || folder;
                      const isSelected = selectedRepos.has(name);
                      const isDisabled = !isSelected && selectedRepos.size >= MAX_IMPORT_SELECTION;
                      return (
                        <div
                          key={name}
                          style={{
                            ...styles.repoRow,
                            background: isSelected ? C.blueLt : C.white,
                            opacity: isDisabled ? 0.5 : 1,
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                          }}
                          onClick={() => !isDisabled && toggleRepoSelection(name)}
                          role="option"
                          aria-selected={isSelected}
                          aria-disabled={isDisabled}
                        >
                          <div style={styles.repoCheckboxCol}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              disabled={isDisabled}
                              onChange={() => toggleRepoSelection(name)}
                              onClick={(e) => e.stopPropagation()}
                              style={styles.repoCheckbox}
                              aria-label={`Select ${name}`}
                            />
                          </div>
                          <div style={styles.repoNameCol}>
                            <span style={styles.repoNameText}>📁 {name}</span>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )
              ) : (
                /* SCM repo browser */
                reposLoading ? (
                  <div style={styles.repoLoadingState}>
                    Loading repositories…
                  </div>
                ) : reposError ? (
                  <div style={styles.repoErrorState} role="alert">
                    <span style={styles.repoErrorIcon}>⚠</span>
                    <span>{reposError}</span>
                    <button
                      onClick={() => fetchRepos(selectedConnectionId)}
                      style={styles.repoRetryBtn}
                    >
                      Retry
                    </button>
                  </div>
                ) : repos.length === 0 ? (
                  <div style={styles.repoEmptyState}>
                    No repositories found in this SCM connection.
                  </div>
                ) : (
                  <>
                    {/* Table header */}
                    <div style={styles.repoTableHeader}>
                      <div style={styles.repoCheckboxCol}></div>
                      <div style={styles.repoNameCol}>Name</div>
                      <div style={styles.repoLangCol}>Language</div>
                      <div style={styles.repoDateCol}>Last Updated</div>
                    </div>

                    {/* Table rows */}
                    {paginatedRepos.map(repo => {
                      const isSelected = selectedRepos.has(repo.name);
                      const isDisabled = !isSelected && selectedRepos.size >= MAX_IMPORT_SELECTION;
                      return (
                        <div
                          key={repo.name}
                          style={{
                            ...styles.repoRow,
                            background: isSelected ? C.blueLt : C.white,
                            opacity: isDisabled ? 0.5 : 1,
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                          }}
                          onClick={() => !isDisabled && toggleRepoSelection(repo.name)}
                          role="option"
                          aria-selected={isSelected}
                          aria-disabled={isDisabled}
                        >
                          <div style={styles.repoCheckboxCol}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              disabled={isDisabled}
                              onChange={() => toggleRepoSelection(repo.name)}
                              onClick={(e) => e.stopPropagation()}
                              style={styles.repoCheckbox}
                              aria-label={`Select ${repo.name}`}
                            />
                          </div>
                          <div style={styles.repoNameCol}>
                            <span style={styles.repoNameText}>{repo.name}</span>
                          </div>
                          <div style={styles.repoLangCol}>
                            <span style={styles.repoLangBadge}>
                              {repo.language || '—'}
                            </span>
                          </div>
                          <div style={styles.repoDateCol}>
                            <span style={styles.repoDateText}>
                              {formatTimestamp(repo.last_updated || repo.updated_at)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )
              )}
            </div>

            {/* Pagination */}
            {!(isFilesystemProject ? fsFoldersLoading : reposLoading) && !(isFilesystemProject ? fsFoldersError : reposError) && totalRepoPages > 1 && (
              <div style={styles.repoPagination}>
                <button
                  onClick={() => setRepoPage(p => Math.max(1, p - 1))}
                  disabled={repoPage === 1}
                  style={{
                    ...styles.repoPageBtn,
                    opacity: repoPage === 1 ? 0.4 : 1,
                    cursor: repoPage === 1 ? 'not-allowed' : 'pointer',
                  }}
                  aria-label="Previous page"
                >
                  ← Prev
                </button>
                <span style={styles.repoPageInfo}>
                  Page {repoPage} of {totalRepoPages} ({browseItems.length} {isFilesystemProject ? 'folders' : 'repos'})
                </span>
                <button
                  onClick={() => setRepoPage(p => Math.min(totalRepoPages, p + 1))}
                  disabled={repoPage === totalRepoPages}
                  style={{
                    ...styles.repoPageBtn,
                    opacity: repoPage === totalRepoPages ? 0.4 : 1,
                    cursor: repoPage === totalRepoPages ? 'not-allowed' : 'pointer',
                  }}
                  aria-label="Next page"
                >
                  Next →
                </button>
              </div>
            )}

            {/* Footer actions */}
            <div style={styles.repoBrowserFooter}>
              <button onClick={closeBulkImport} style={styles.cancelBtn}>
                Cancel
              </button>
              <button
                onClick={() => handleBulkImport([...selectedRepos])}
                disabled={selectedRepos.size === 0}
                style={{
                  ...styles.importBtn,
                  opacity: selectedRepos.size === 0 ? 0.5 : 1,
                  cursor: selectedRepos.size === 0 ? 'not-allowed' : 'pointer',
                }}
              >
                Import {selectedRepos.size > 0 ? `(${selectedRepos.size})` : ''} Selected
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </PageLayout>
  );
}


// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = {
  container: {
    padding: '24px 32px',
    maxWidth: 1060,
    margin: '0 auto',
    position: 'relative',
  },
  toast: {
    position: 'fixed',
    top: 20,
    right: 20,
    padding: '10px 20px',
    borderRadius: 8,
    color: C.white,
    fontSize: 13,
    fontWeight: 600,
    zIndex: 10001,
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 24,
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    color: C.text,
    margin: 0,
  },
  subtitle: {
    fontSize: 13,
    color: C.slate,
    margin: '4px 0 0',
  },
  headerActions: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
  },
  createBtn: {
    padding: '9px 18px',
    borderRadius: 8,
    border: 'none',
    background: C.blue,
    color: C.white,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 2px 6px rgba(59,130,246,0.3)',
  },
  bulkImportBtn: {
    padding: '9px 18px',
    borderRadius: 8,
    border: `1px solid ${C.border}`,
    background: C.white,
    color: C.text,
    fontSize: 13,
    fontWeight: 600,
  },
  scmWarning: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 14px',
    borderRadius: 8,
    background: C.yellowLt,
    border: `1px solid ${C.yellow}30`,
    fontSize: 12,
    color: C.textMid,
    marginBottom: 16,
  },
  scmWarningIcon: {
    fontSize: 14,
    fontWeight: 700,
    color: C.yellow,
    flexShrink: 0,
  },
  importConfirmation: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 14px',
    borderRadius: 8,
    background: '#D1FAE5',
    border: `1px solid ${C.green}30`,
    fontSize: 12,
    color: '#065F46',
    marginBottom: 16,
  },
  importConfirmationIcon: {
    fontSize: 14,
    fontWeight: 700,
    color: C.green,
    flexShrink: 0,
  },
  dismissBtn: {
    marginLeft: 'auto',
    background: 'none',
    border: 'none',
    fontSize: 14,
    color: '#065F46',
    cursor: 'pointer',
    padding: '2px 6px',
    borderRadius: 4,
  },
  // Form
  formCard: {
    background: C.white,
    border: `1px solid ${C.border}`,
    borderRadius: 10,
    padding: '20px 24px',
    marginBottom: 24,
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  },
  formTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: C.text,
    margin: '0 0 16px',
  },
  fieldGroup: {
    marginBottom: 16,
    position: 'relative',
  },
  label: {
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    color: C.textMid,
    marginBottom: 4,
  },
  required: {
    color: C.red,
  },
  input: {
    width: '100%',
    padding: '9px 12px',
    fontSize: 13,
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    outline: 'none',
    color: C.text,
    background: C.white,
    boxSizing: 'border-box',
  },
  fieldError: {
    display: 'block',
    fontSize: 11,
    color: C.red,
    marginTop: 4,
  },
  charCount: {
    position: 'absolute',
    right: 0,
    top: 0,
    fontSize: 10,
    color: C.slateLight,
  },
  sourceError: {
    padding: '8px 12px',
    borderRadius: 6,
    background: C.redLt,
    color: C.red,
    fontSize: 11,
    fontWeight: 500,
    marginBottom: 16,
    border: `1px solid ${C.red}20`,
  },
  apiError: {
    padding: '10px 14px',
    borderRadius: 6,
    background: C.redLt,
    color: C.red,
    fontSize: 12,
    fontWeight: 500,
    marginBottom: 16,
    border: `1px solid ${C.red}20`,
  },
  formActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
  },
  cancelBtn: {
    padding: '8px 16px',
    borderRadius: 6,
    border: `1px solid ${C.border}`,
    background: C.white,
    color: C.textMid,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },
  submitBtn: {
    padding: '8px 18px',
    borderRadius: 6,
    border: 'none',
    background: C.blue,
    color: C.white,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },
  // List
  listError: {
    padding: '12px 16px',
    borderRadius: 8,
    background: C.redLt,
    color: C.red,
    fontSize: 12,
    marginBottom: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  retryBtn: {
    padding: '4px 12px',
    borderRadius: 4,
    border: `1px solid ${C.red}`,
    background: 'transparent',
    color: C.red,
    fontSize: 11,
    fontWeight: 600,
    cursor: 'pointer',
  },
  loadingState: {
    textAlign: 'center',
    padding: '40px 0',
    color: C.slate,
    fontSize: 13,
    fontStyle: 'italic',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    background: C.white,
    border: `1px solid ${C.border}`,
    borderRadius: 10,
  },
  emptyText: {
    fontSize: 14,
    color: C.slate,
    margin: '0 0 12px',
  },
  emptyBtn: {
    padding: '9px 18px',
    borderRadius: 8,
    border: 'none',
    background: C.blue,
    color: C.white,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  // Import progress
  importProgressBar: {
    padding: '12px 16px',
    borderRadius: 8,
    background: C.blueLt,
    border: `1px solid ${C.blue}30`,
    marginBottom: 16,
  },
  importProgressText: {
    fontSize: 12,
    color: C.text,
    fontWeight: 600,
    marginBottom: 8,
  },
  progressTrack: {
    height: 6,
    borderRadius: 3,
    background: '#E0E7FF',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 3,
    background: C.blue,
    transition: 'width 0.3s ease',
  },
  // Table
  tableWrapper: {
    background: C.white,
    border: `1px solid ${C.border}`,
    borderRadius: 10,
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 13,
  },
  th: {
    textAlign: 'left',
    padding: '12px 16px',
    fontSize: 11,
    fontWeight: 700,
    color: C.slate,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: `1px solid ${C.border}`,
    background: C.bg,
    userSelect: 'none',
  },
  tr: {
    borderBottom: `1px solid ${C.border}`,
  },
  td: {
    padding: '12px 16px',
    verticalAlign: 'middle',
  },
  appNameCell: {
    fontWeight: 600,
    color: C.text,
  },
  sourceBadge: {
    fontSize: 11,
    fontWeight: 600,
    padding: '3px 8px',
    borderRadius: 4,
    background: C.bg,
    color: C.textMid,
    border: `1px solid ${C.border}`,
  },
  langCell: {
    color: C.textMid,
  },
  fwCell: {
    color: C.textMid,
  },
  timestampCell: {
    color: C.slateLight,
    fontSize: 12,
  },
  // Pagination
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
    marginTop: 16,
    padding: '12px 0',
  },
  pageBtn: {
    padding: '6px 14px',
    borderRadius: 6,
    border: `1px solid ${C.border}`,
    background: C.white,
    color: C.text,
    fontSize: 12,
    fontWeight: 600,
  },
  pageInfo: {
    fontSize: 12,
    color: C.slate,
  },
  // Dialog / Overlay
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(15, 23, 42, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
  },
  dialog: {
    background: C.white,
    borderRadius: 12,
    padding: '24px 28px',
    maxWidth: 500,
    width: '90%',
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
  },
  dialogTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: C.text,
    margin: '0 0 12px',
  },
  dialogText: {
    fontSize: 13,
    color: C.textMid,
    margin: '0 0 20px',
    lineHeight: 1.5,
  },
  dialogActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
  },
  // Repo Browser Dialog
  repoBrowserDialog: {
    background: C.white,
    borderRadius: 12,
    width: '95%',
    maxWidth: 720,
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 60px rgba(0,0,0,0.2), 0 4px 16px rgba(0,0,0,0.1)',
    overflow: 'hidden',
  },
  repoBrowserHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '20px 24px 12px',
    borderBottom: `1px solid ${C.border}`,
  },
  repoBrowserSubtitle: {
    fontSize: 12,
    color: C.slate,
    margin: '4px 0 0',
  },
  repoBrowserCloseBtn: {
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    background: 'transparent',
    fontSize: 16,
    color: C.slate,
    cursor: 'pointer',
    borderRadius: 6,
    flexShrink: 0,
  },
  connectionSelector: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 24px',
    borderBottom: `1px solid ${C.border}`,
    background: C.bg,
  },
  connectionLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: C.textMid,
    whiteSpace: 'nowrap',
  },
  connectionSelect: {
    flex: 1,
    padding: '6px 10px',
    fontSize: 12,
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    color: C.text,
    background: C.white,
    outline: 'none',
  },
  selectionBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 24px',
    borderBottom: `1px solid ${C.border}`,
    background: C.bg,
  },
  selectionCount: {
    fontSize: 12,
    fontWeight: 600,
    color: C.textMid,
  },
  selectionActions: {
    display: 'flex',
    gap: 8,
  },
  selectionActionBtn: {
    padding: '4px 10px',
    borderRadius: 4,
    border: `1px solid ${C.border}`,
    background: C.white,
    color: C.textMid,
    fontSize: 11,
    fontWeight: 600,
    cursor: 'pointer',
  },
  repoListContainer: {
    flex: 1,
    overflowY: 'auto',
    minHeight: 200,
    maxHeight: 400,
  },
  repoLoadingState: {
    textAlign: 'center',
    padding: '40px 0',
    color: C.slate,
    fontSize: 13,
    fontStyle: 'italic',
  },
  repoErrorState: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '16px 24px',
    color: C.red,
    fontSize: 12,
  },
  repoErrorIcon: {
    fontSize: 16,
    flexShrink: 0,
  },
  repoRetryBtn: {
    marginLeft: 'auto',
    padding: '4px 12px',
    borderRadius: 4,
    border: `1px solid ${C.red}`,
    background: 'transparent',
    color: C.red,
    fontSize: 11,
    fontWeight: 600,
    cursor: 'pointer',
    flexShrink: 0,
  },
  repoEmptyState: {
    textAlign: 'center',
    padding: '40px 0',
    color: C.slate,
    fontSize: 13,
  },
  repoTableHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 24px',
    borderBottom: `1px solid ${C.border}`,
    background: C.bg,
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  repoRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 24px',
    borderBottom: `1px solid ${C.border}`,
    transition: 'background 0.1s',
  },
  repoCheckboxCol: {
    width: 36,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  repoCheckbox: {
    width: 16,
    height: 16,
    cursor: 'pointer',
    accentColor: C.blue,
  },
  repoNameCol: {
    flex: 2,
    minWidth: 0,
    paddingRight: 12,
  },
  repoNameText: {
    fontSize: 13,
    fontWeight: 600,
    color: C.text,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'block',
  },
  repoLangCol: {
    flex: 1,
    minWidth: 0,
    paddingRight: 12,
  },
  repoLangBadge: {
    fontSize: 11,
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: 4,
    background: C.bg,
    color: C.textMid,
    border: `1px solid ${C.border}`,
  },
  repoDateCol: {
    flex: 1,
    minWidth: 0,
  },
  repoDateText: {
    fontSize: 12,
    color: C.slateLight,
  },
  repoPagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
    padding: '12px 24px',
    borderTop: `1px solid ${C.border}`,
    background: C.bg,
  },
  repoPageBtn: {
    padding: '5px 12px',
    borderRadius: 6,
    border: `1px solid ${C.border}`,
    background: C.white,
    color: C.text,
    fontSize: 11,
    fontWeight: 600,
  },
  repoPageInfo: {
    fontSize: 11,
    color: C.slate,
  },
  repoBrowserFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
    padding: '14px 24px',
    borderTop: `1px solid ${C.border}`,
    background: C.white,
  },
  importBtn: {
    padding: '9px 18px',
    borderRadius: 8,
    border: 'none',
    background: C.green,
    color: C.white,
    fontSize: 13,
    fontWeight: 600,
    boxShadow: '0 2px 6px rgba(16,185,129,0.3)',
  },
};
